// src/pages/itsm/ITSMTicketDetail.tsx
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
// @ts-ignore
import { supabase } from '../../supabaseClient'
import { Sun, Moon } from 'lucide-react'
import { useCurrentAgent } from '../../hooks/useCurrentAgent'
import { useTheme } from '../../hooks/useTheme'
import { Modal, Btn, showToast, ToastHost, Card, Pill, Eyebrow, T } from '../../components/ui/UI'

const STATUS_FLOW: Record<string, string[]> = {
    open: ['in_progress', 'pending'],
    pending: ['in_progress', 'closed'],
    in_progress: ['pending_approval', 'resolved'],
    pending_approval: ['approved', 'rejected'],
    approved: ['in_progress', 'resolved'],
    rejected: ['open'],
    resolved: ['closed'],
}

const STATUS_CONFIG: Record<string, { label: string }> = {
    open: { label: 'Open' },
    pending: { label: 'Pending' },
    in_progress: { label: 'In Progress' },
    pending_approval: { label: 'Pending Approval' },
    approved: { label: 'Approved' },
    rejected: { label: 'Rejected' },
    resolved: { label: 'Resolved' },
    closed: { label: 'Closed' },
}

// status -> Pill tone. Kept separate from STATUS_CONFIG's label so the
// design-system tokens (T.*) drive color instead of hardcoded hex.
const STATUS_TONE: Record<string, string> = {
    open: 'info',
    pending: 'neutral',
    in_progress: 'warning',
    pending_approval: 'violet',
    approved: 'success',
    rejected: 'danger',
    resolved: 'success',
    closed: 'neutral',
}

function toneColor(tone: string): { bg: string; color: string; border: string } {
    switch (tone) {
        case 'success': return { bg: T.successBg, color: T.success, border: T.successBorder }
        case 'warning': return { bg: T.warningBg, color: T.warning, border: T.warningBorder }
        case 'danger': return { bg: T.dangerBg, color: T.danger, border: T.dangerBorder }
        case 'info': return { bg: T.infoBg, color: T.info, border: T.infoBorder }
        case 'violet': return { bg: T.violetBg, color: T.violet, border: T.violetBorder }
        default: return { bg: T.surface2, color: T.text2, border: T.border }
    }
}

export default function ITSMTicketDetail() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { agent, loading: agentLoading, notProvisioned } = useCurrentAgent()
    const { theme, toggleTheme } = useTheme()
    const [ticket, setTicket] = useState<any>(null)
    const [comments, setComments] = useState<any[]>([])
    const [agents, setAgents] = useState<any[]>([])
    const [teams, setTeams] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [comment, setComment] = useState('')
    const [isPrivate, setIsPrivate] = useState(false)
    const [sending, setSending] = useState(false)
    const [actionLoading, setActionLoading] = useState(false)

    // Site-visit scheduling — only meaningful for tickets that came through
    // the maintenance-contract/visit-quota gated client flow (contract_id
    // is set). itsm_ticket_visits is kept separate from the legacy
    // ticket_visits table (which FKs to the old `tickets` table).
    const [visits, setVisits] = useState<any[]>([])
    const [showVisitModal, setShowVisitModal] = useState(false)
    const [visitApprovesTicket, setVisitApprovesTicket] = useState(false)
    const [visitForm, setVisitForm] = useState({
        visit_date: '', visit_time: '', technician_name: '', technician_phone: '', technician_id_number: '', notes: '',
    })
    const [approvalNotes, setApprovalNotes] = useState<Record<number, string>>({})

    // Completion report upload — ports SupportTicketDetail.tsx's
    // handleUploadReport (legacy `tickets` table) onto itsm_tickets, and
    // switches the hyper-task call from a raw, unauthenticated fetch() (the
    // legacy call 401s silently since the function requires a JWT) to
    // supabase.functions.invoke(), which attaches the session JWT for us.
    const [uploadingReport, setUploadingReport] = useState(false)

    // completion-reports is a private bucket (ticket-attachments too) — the
    // DB only ever stores the storage path, never a public URL, so a signed,
    // time-limited URL is generated here whenever the report/signature path
    // changes rather than persisted. Regenerated on every ticket refetch.
    const [reportUrl, setReportUrl] = useState<string | null>(null)
    const [signatureUrl, setSignatureUrl] = useState<string | null>(null)

    useEffect(() => { fetchAll() }, [id])

    useEffect(() => {
        let cancelled = false
        const loadSignedUrls = async () => {
            if (ticket?.completion_report_url) {
                const { data } = await supabase.storage.from('completion-reports')
                    .createSignedUrl(ticket.completion_report_url, 3600)
                if (!cancelled) setReportUrl(data?.signedUrl || null)
            } else if (!cancelled) setReportUrl(null)

            if (ticket?.client_signature_url) {
                const { data } = await supabase.storage.from('completion-reports')
                    .createSignedUrl(ticket.client_signature_url, 3600)
                if (!cancelled) setSignatureUrl(data?.signedUrl || null)
            } else if (!cancelled) setSignatureUrl(null)
        }
        loadSignedUrls()
        return () => { cancelled = true }
    }, [ticket?.completion_report_url, ticket?.client_signature_url])

    const fetchAll = async () => {
        const [{ data: t }, { data: c }, { data: a }, { data: tm }, { data: v }] = await Promise.all([
            supabase.from('itsm_tickets').select(`
                *,
                itsm_services(name, sla_hours),
                itsm_service_categories(name, icon, color),
                itsm_agents!assigned_agent_id(id, name, email, avatar),
                itsm_teams!assigned_team_id(id, name),
                itsm_approvals(*, itsm_agents(name)),
                client_profiles!requester_id(full_name, company_name, phone, email)
            `).eq('id', id).single(),
            supabase.from('itsm_ticket_comments').select('*').eq('ticket_id', id).order('created_at'),
            supabase.from('itsm_agents').select('id, name').eq('is_active', true),
            supabase.from('itsm_teams').select('id, name').eq('is_active', true),
            supabase.from('itsm_ticket_visits').select('*').eq('ticket_id', id).order('scheduled_at', { ascending: false }),
        ])
        setTicket(t)
        setComments(c || [])
        setAgents(a || [])
        setTeams(tm || [])
        setVisits(v || [])
        setLoading(false)
    }

    const changeStatus = async (newStatus: string) => {
        setActionLoading(true)
        await supabase.from('itsm_tickets').update({
            status: newStatus,
            ...(newStatus === 'resolved' ? { resolved_at: new Date().toISOString() } : {}),
            ...(newStatus === 'closed' ? { closed_at: new Date().toISOString() } : {}),
        }).eq('id', id)

        await supabase.from('itsm_activity_log').insert([{
            ticket_id: Number(id),
            action: 'status_changed',
            old_value: ticket.status,
            new_value: newStatus,
            performed_by: agent?.name || 'Agent',
            performed_by_type: 'agent',
        }])
        await fetchAll()
        setActionLoading(false)
    }

    const assignAgent = async (agentId: string) => {
        await supabase.from('itsm_tickets').update({ assigned_agent_id: agentId || null }).eq('id', id)
        await fetchAll()
    }

    const assignTeam = async (teamId: string) => {
        await supabase.from('itsm_tickets').update({ assigned_team_id: teamId || null }).eq('id', id)
        await fetchAll()
    }

    const sendComment = async () => {
        if (!comment.trim()) return
        setSending(true)
        await supabase.from('itsm_ticket_comments').insert([{
            ticket_id: Number(id),
            author_type: 'agent',
            author_name: agent?.name || 'Support Agent',
            message: comment,
            is_private: isPrivate,
        }])
        setComment('')
        await fetchAll()
        setSending(false)
    }

    const createPO = () => navigate(`/dashboard/itsm/po/new?ticket_id=${id}`)

    const handleUploadReport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        setUploadingReport(true)
        try {
            const path = `itsm/${ticket.id}/report_${Date.now()}.pdf`
            const { error: uploadError } = await supabase.storage
                .from('completion-reports')
                .upload(path, file, { contentType: 'application/pdf' })
            if (uploadError) throw uploadError

            // completion-reports is a private bucket — completion_report_url
            // stores the storage PATH, not a public URL. A signed URL is
            // generated on demand when the report/signature are displayed
            // (see reportUrl/signatureUrl state below).
            await supabase.from('itsm_tickets').update({
                completion_report_url: path,
                report_uploaded_at: new Date().toISOString(),
                report_uploaded_by: agent?.agentId || null,
            }).eq('id', ticket.id)

            const { error: fnError } = await supabase.functions.invoke('hyper-task', {
                body: {
                    type: 'itsm_completion_report_ready',
                    data: {
                        ticketId: ticket.id,
                        ticket_number: ticket.ticket_number,
                        title: ticket.title,
                        full_name: ticket.client_profiles?.full_name,
                        email: ticket.client_profiles?.email,
                    },
                },
            })
            if (fnError) throw fnError

            showToast('Report uploaded — client has been notified', 'success')
            await fetchAll()
        } catch (err: any) {
            console.error('Report upload error:', err)
            showToast('❌ Failed to upload report. Please try again.', 'error')
        }
        setUploadingReport(false)
        e.target.value = ''
    }

    // Opens the visit-scheduling modal. `approvesTicket` distinguishes the two
    // cases this can happen from: a service that requires formal approval
    // (status pending_approval -> approved, so scheduling the visit IS what
    // "approve" means for these), vs. a visit-gated service that doesn't
    // require approval at all (status starts at 'open' — scheduling the visit
    // is a standalone action that also nudges status to in_progress).
    const openVisitModal = (approvesTicket: boolean) => {
        setVisitApprovesTicket(approvesTicket)
        setVisitForm({ visit_date: '', visit_time: '', technician_name: '', technician_phone: '', technician_id_number: '', notes: '' })
        setShowVisitModal(true)
    }

    const submitVisit = async () => {
        const { visit_date, visit_time, technician_name, technician_phone, technician_id_number } = visitForm
        if (!visit_date || !visit_time || !technician_name || !technician_phone || !technician_id_number) {
            showToast('Fill in all required visit fields', 'error')
            return
        }
        setActionLoading(true)
        await supabase.from('itsm_ticket_visits').insert([{
            ticket_id: Number(id),
            ...visitForm,
            scheduled_by: agent?.agentId || null,
        }])

        if (visitApprovesTicket) {
            await changeStatus('approved')
        } else if (ticket.status === 'open') {
            await changeStatus('in_progress')
        } else {
            await fetchAll()
        }
        setShowVisitModal(false)
        setActionLoading(false)
        showToast('Visit scheduled', 'success')
    }

    // Approvals are sequential by level — only the lowest still-pending level
    // is actionable. Approving the last pending level flips the ticket to
    // 'approved' (or opens the visit modal first, for contract-gated tickets
    // that haven't had a visit scheduled yet); rejecting any level rejects
    // the whole ticket immediately.
    const pendingApprovals = (ticket?.itsm_approvals || []).filter((a: any) => a.status === 'pending').sort((a: any, b: any) => a.level - b.level)
    const lowestPendingApproval = pendingApprovals[0] || null

    const decideApproval = async (approvalId: number, decision: 'approved' | 'rejected') => {
        setActionLoading(true)
        await supabase.from('itsm_approvals').update({
            status: decision,
            notes: approvalNotes[approvalId] || null,
            decided_at: new Date().toISOString(),
            approver_id: agent?.agentId || null,
            approver_name: agent?.name || null,
        }).eq('id', approvalId)

        if (decision === 'rejected') {
            await changeStatus('rejected')
            setActionLoading(false)
            return
        }

        const isLastLevel = pendingApprovals.length <= 1
        if (isLastLevel) {
            if (ticket.contract_id && visits.length === 0) {
                // Final approval level cleared, but this contract-gated ticket
                // still needs a technician visit scheduled before it can move
                // to 'approved' — collect that now rather than leaving the
                // ticket in a state where it's "approved" with no visit.
                await fetchAll()
                openVisitModal(true)
            } else {
                await changeStatus('approved')
            }
        } else {
            await fetchAll()
        }
        setActionLoading(false)
    }

    if (loading || agentLoading) return (
        <div className="jr-app" data-theme={theme}>
            <div style={styles.loading}>Loading...</div>
        </div>
    )
    if (!ticket) return (
        <div className="jr-app" data-theme={theme}>
            <div style={styles.loading}>Ticket not found</div>
        </div>
    )
    if (notProvisioned) return (
        <div className="jr-app" data-theme={theme}>
            <div style={styles.loading}>
                You're not provisioned as an ITSM agent yet — contact an ITSM admin to get set up before working on tickets.
            </div>
        </div>
    )

    const status = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open
    const statusTone = STATUS_TONE[ticket.status] || 'neutral'
    const nextStatuses = STATUS_FLOW[ticket.status] || []
    const s = styles

    return (
        <div className="jr-app" data-theme={theme}>
            <div style={s.page}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <button onClick={() => navigate('/dashboard/itsm/tickets')} style={s.back}>← Back to tickets</button>
                    <button onClick={toggleTheme} aria-label="Toggle theme" style={s.themeToggle}>
                        {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
                    </button>
                </div>

                {/* Header */}
                <div style={s.header}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                            <span style={s.ticketNum}>{ticket.ticket_number}</span>
                            <Pill tone={statusTone}>{status.label}</Pill>
                            <Pill tone="neutral">{ticket.type?.replace('_', ' ').toUpperCase()}</Pill>
                            {ticket.sla_breached && (
                                <Pill tone="danger">SLA BREACHED</Pill>
                            )}
                        </div>
                        <h1 style={s.title}>{ticket.title}</h1>
                        <p style={s.meta}>
                            {ticket.itsm_service_categories?.icon} {ticket.itsm_service_categories?.name} →{' '}
                            {ticket.itsm_services?.name} · Created {new Date(ticket.created_at).toLocaleDateString('en-GB')}
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
                        {nextStatuses.map(next => {
                            // Approving a contract-gated ticket that hasn't had a
                            // technician visit scheduled yet requires collecting
                            // that first — and, like all approve/reject decisions,
                            // is restricted to ITSM admins.
                            const isApproveStep = next === 'approved'
                            const needsVisitFirst = isApproveStep && ticket.contract_id && visits.length === 0
                            if (isApproveStep && !agent?.isItsmAdmin) return null
                            const tc = toneColor(STATUS_TONE[next] || 'neutral')
                            return (
                                <button key={next}
                                    style={{ height: 34, padding: '0 14px', background: tc.bg, color: tc.color, border: `0.5px solid ${tc.border}`, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                                    onClick={() => needsVisitFirst ? openVisitModal(true) : changeStatus(next)} disabled={actionLoading}>
                                    → {needsVisitFirst ? 'Approve & Schedule Visit' : STATUS_CONFIG[next]?.label}
                                </button>
                            )
                        })}
                        {ticket.contract_id && visits.length === 0 && !nextStatuses.includes('approved') && agent?.isItsmAdmin && (
                            <button style={{ height: 34, padding: '0 14px', background: T.infoBg, color: T.info, border: `0.5px solid ${T.infoBorder}`, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                                onClick={() => openVisitModal(false)} disabled={actionLoading}>
                                🔧 Schedule Technician Visit
                            </button>
                        )}
                        <button style={s.btnSecondary} onClick={createPO}>📦 Create PO</button>
                    </div>
                </div>

                <div style={s.grid}>
                    <div style={s.main}>
                        {/* Description */}
                        <Card>
                            <Eyebrow>Description</Eyebrow>
                            <p style={{ fontSize: 14, color: T.text2, lineHeight: 1.6 }}>{ticket.description}</p>
                        </Card>

                        {/* Approvals — sequential by level; only the lowest still-pending
                            level is actionable, and only for ITSM admins. */}
                        {ticket.itsm_approvals?.length > 0 && (
                            <Card>
                                <Eyebrow>Approvals</Eyebrow>
                                {[...ticket.itsm_approvals].sort((a: any, b: any) => a.level - b.level).map((a: any) => {
                                    const isActionable = a.status === 'pending' && lowestPendingApproval?.id === a.id && agent?.isItsmAdmin
                                    const approvalTone = a.status === 'approved' ? 'success' : a.status === 'rejected' ? 'danger' : 'neutral'
                                    return (
                                        <div key={a.id} style={{ padding: '10px 0', borderBottom: `0.5px solid ${T.border}` }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <p style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Level {a.level}{a.approver_name ? ` — ${a.approver_name}` : ''}</p>
                                                    {a.notes && <p style={{ fontSize: 12, color: T.text2 }}>{a.notes}</p>}
                                                </div>
                                                <Pill tone={approvalTone}>{a.status.toUpperCase()}</Pill>
                                            </div>
                                            {isActionable && (
                                                <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                                                    <input
                                                        style={{ ...s.input, height: 30, flex: 1 }}
                                                        placeholder="Notes (optional)"
                                                        value={approvalNotes[a.id] || ''}
                                                        onChange={e => setApprovalNotes({ ...approvalNotes, [a.id]: e.target.value })}
                                                    />
                                                    <Btn disabled={actionLoading} onClick={() => decideApproval(a.id, 'approved')}
                                                        style={{ height: 30, padding: '0 12px', background: T.successBg, color: T.success, borderColor: T.successBorder, fontSize: 12, fontWeight: 700 }}>
                                                        Approve
                                                    </Btn>
                                                    <Btn disabled={actionLoading} onClick={() => decideApproval(a.id, 'rejected')}
                                                        style={{ height: 30, padding: '0 12px', background: T.dangerBg, color: T.danger, borderColor: T.dangerBorder, fontSize: 12, fontWeight: 700 }}>
                                                        Reject
                                                    </Btn>
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </Card>
                        )}

                        {/* Site Visit — only for tickets that came through the
                            maintenance-contract/visit-quota gated client flow. */}
                        {ticket.contract_id && (
                            <Card>
                                <Eyebrow>Site Visit</Eyebrow>
                                {visits.length === 0 ? (
                                    <p style={{ fontSize: 13, color: T.text3 }}>No technician visit scheduled yet.</p>
                                ) : (
                                    visits.map(v => (
                                        <div key={v.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, padding: '10px 0', borderBottom: `0.5px solid ${T.border}` }}>
                                            <div><p style={s.visitLabel}>Date</p><p style={s.visitVal}>{new Date(v.visit_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p></div>
                                            <div><p style={s.visitLabel}>Time</p><p style={s.visitVal}>{v.visit_time}</p></div>
                                            <div><p style={s.visitLabel}>Technician</p><p style={s.visitVal}>{v.technician_name}</p></div>
                                            <div><p style={s.visitLabel}>Mobile</p><p style={s.visitVal}>{v.technician_phone}</p></div>
                                            <div><p style={s.visitLabel}>ID number</p><p style={s.visitVal}>{v.technician_id_number}</p></div>
                                            {v.notes && <div style={{ gridColumn: '1/-1' }}><p style={s.visitLabel}>Notes</p><p style={s.visitVal}>{v.notes}</p></div>}
                                        </div>
                                    ))
                                )}
                            </Card>
                        )}

                        {/* Completion Report — for tickets that came through the
                            maintenance-contract/visit-gated flow, once a visit
                            has been scheduled. Admin uploads a PDF; the client
                            then reviews and signs it (client ITSMTicketDetail.tsx). */}
                        {ticket.contract_id && (
                            <Card>
                                <Eyebrow>Completion Report</Eyebrow>
                                {ticket.completion_report_url ? (
                                    <div>
                                        {reportUrl ? (
                                            <a href={reportUrl} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: T.accent, fontWeight: 700, textDecoration: 'none' }}>
                                                📄 View uploaded report →
                                            </a>
                                        ) : (
                                            <p style={{ fontSize: 13, color: T.text3 }}>Loading report link...</p>
                                        )}
                                        <p style={{ fontSize: 12, color: T.text3, marginTop: 6 }}>
                                            Uploaded {ticket.report_uploaded_at ? new Date(ticket.report_uploaded_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                                        </p>
                                        {ticket.signed_at ? (
                                            <p style={{ fontSize: 12, color: T.success, fontWeight: 700, marginTop: 10 }}>
                                                ✓ Signed by client on {new Date(ticket.signed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                            </p>
                                        ) : (
                                            <p style={{ fontSize: 12, color: T.warning, fontWeight: 700, marginTop: 10 }}>
                                                Awaiting client signature
                                            </p>
                                        )}
                                        {signatureUrl && (
                                            <img src={signatureUrl} alt="Client signature" style={{ maxWidth: 220, marginTop: 10, border: `0.5px solid ${T.border}`, borderRadius: 6, background: T.surface }} />
                                        )}
                                    </div>
                                ) : agent?.isItsmAdmin ? (
                                    <div>
                                        <p style={{ fontSize: 12, color: T.text3, marginBottom: 12 }}>
                                            Upload the completion report (PDF) for the client to review and sign.
                                        </p>
                                        <label style={{
                                            display: 'inline-flex', alignItems: 'center', height: 34, padding: '0 14px',
                                            background: uploadingReport ? T.text3 : T.accent, color: T.accentFg, borderRadius: 8,
                                            fontSize: 12, fontWeight: 700, cursor: uploadingReport ? 'default' : 'pointer',
                                        }}>
                                            {uploadingReport ? 'Uploading...' : '📤 Upload Report (PDF)'}
                                            <input type="file" accept="application/pdf" onChange={handleUploadReport} disabled={uploadingReport} style={{ display: 'none' }} />
                                        </label>
                                    </div>
                                ) : (
                                    // Report upload is an ITSM-admin action, same as approve/reject
                                    // and visit scheduling — a plain agent can view this ticket but
                                    // not close it out with a completion report.
                                    <p style={{ fontSize: 12, color: T.text3 }}>No report uploaded yet.</p>
                                )}
                            </Card>
                        )}

                        {/* Activity Log */}
                        <Card>
                            <Eyebrow>Conversation</Eyebrow>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                                {comments.length === 0 && <p style={{ fontSize: 13, color: T.text3 }}>No messages yet</p>}
                                {comments.map(c => (
                                    <div key={c.id} style={{
                                        padding: '10px 14px',
                                        background: c.is_private ? T.warningBg : c.author_type === 'agent' ? T.infoBg : T.surface2,
                                        borderLeft: `3px solid ${c.is_private ? T.warning : c.author_type === 'agent' ? T.info : T.border}`,
                                        borderRadius: '0 6px 6px 0',
                                    }}>
                                        <p style={{ fontSize: 11, color: T.text3, marginBottom: 4, fontWeight: 600 }}>
                                            {c.author_name} {c.is_private && '🔒 Private'} · {new Date(c.created_at).toLocaleDateString('en-GB')}
                                        </p>
                                        <p style={{ fontSize: 13, color: T.text2, lineHeight: 1.5 }}>{c.message}</p>
                                    </div>
                                ))}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <textarea
                                    style={{ width: '100%', height: 80, border: `0.5px solid ${T.borderStrong}`, borderRadius: 8, padding: '8px 10px', fontSize: 13, resize: 'none', outline: 'none', background: T.surface, color: T.text }}
                                    placeholder="Reply or add a note..."
                                    value={comment}
                                    onChange={e => setComment(e.target.value)}
                                />
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.text2, cursor: 'pointer' }}>
                                        <input type="checkbox" checked={isPrivate} onChange={e => setIsPrivate(e.target.checked)} />
                                        Private note (not visible to requester)
                                    </label>
                                    <Btn variant="primary" style={undefined} onClick={sendComment} disabled={sending || !comment.trim()}>
                                        {sending ? 'Sending...' : 'Send'}
                                    </Btn>
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* Sidebar */}
                    <div style={s.sidebar}>
                        {/* Ticket Info */}
                        <Card>
                            <Eyebrow>Ticket Details</Eyebrow>
                            <table style={{ width: '100%' }}>
                                {[
                                    { label: 'Status', value: <Pill tone={statusTone}>{status.label}</Pill> },
                                    { label: 'Priority', value: ticket.priority },
                                    { label: 'Type', value: ticket.type?.replace('_', ' ') },
                                    { label: 'SLA Due', value: ticket.sla_due_at ? new Date(ticket.sla_due_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—' },
                                    { label: 'Created', value: new Date(ticket.created_at).toLocaleDateString('en-GB') },
                                ].map(r => (
                                    <tr key={r.label}>
                                        <td style={{ fontSize: 12, color: T.text3, padding: '5px 0', paddingRight: 12, verticalAlign: 'top' }}>{r.label}</td>
                                        <td style={{ fontSize: 13, color: T.text, fontWeight: 500, padding: '5px 0', textTransform: 'capitalize' }}>{r.value}</td>
                                    </tr>
                                ))}
                            </table>
                        </Card>

                        {/* Requester */}
                        {ticket.client_profiles && (
                            <Card>
                                <Eyebrow>Requester</Eyebrow>
                                <p style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4 }}>{ticket.client_profiles.full_name}</p>
                                <p style={{ fontSize: 12, color: T.text2, marginBottom: 2 }}>{ticket.client_profiles.company_name}</p>
                                <p style={{ fontSize: 12, color: T.text2 }}>{ticket.client_profiles.phone}</p>
                            </Card>
                        )}

                        {/* Assignment */}
                        <Card>
                            <Eyebrow>Assignment</Eyebrow>
                            <div style={s.field}>
                                <label style={s.label}>Assign to Agent</label>
                                <select style={s.input} value={ticket.assigned_agent_id || ''} onChange={e => assignAgent(e.target.value)}>
                                    <option value="">Unassigned</option>
                                    {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                </select>
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Assign to Team</label>
                                <select style={s.input} value={ticket.assigned_team_id || ''} onChange={e => assignTeam(e.target.value)}>
                                    <option value="">No Team</option>
                                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                </select>
                            </div>
                        </Card>
                    </div>
                </div>

                <ToastHost />

                <Modal open={showVisitModal} onClose={() => setShowVisitModal(false)} title="Schedule Technician Visit" width={480}
                    footer={
                        <>
                            <Btn variant="ghost" style={undefined} onClick={() => setShowVisitModal(false)}>Cancel</Btn>
                            <Btn variant="primary" style={undefined} disabled={actionLoading} onClick={submitVisit}>
                                {visitApprovesTicket ? 'Approve & Schedule' : 'Schedule Visit'}
                            </Btn>
                        </>
                    }>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                        <div style={s.field}>
                            <label style={s.label}>Visit date <span style={s.req}>*</span></label>
                            <input type="date" style={s.input} value={visitForm.visit_date} onChange={e => setVisitForm({ ...visitForm, visit_date: e.target.value })} />
                        </div>
                        <div style={s.field}>
                            <label style={s.label}>Visit time <span style={s.req}>*</span></label>
                            <input type="time" style={s.input} value={visitForm.visit_time} onChange={e => setVisitForm({ ...visitForm, visit_time: e.target.value })} />
                        </div>
                    </div>
                    <div style={s.field}>
                        <label style={s.label}>Technician name <span style={s.req}>*</span></label>
                        <input style={s.input} placeholder="Mohammed Ahmed" value={visitForm.technician_name} onChange={e => setVisitForm({ ...visitForm, technician_name: e.target.value })} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                        <div style={s.field}>
                            <label style={s.label}>Mobile <span style={s.req}>*</span></label>
                            <input style={s.input} placeholder="+966 5x xxx xxxx" value={visitForm.technician_phone} onChange={e => setVisitForm({ ...visitForm, technician_phone: e.target.value })} />
                        </div>
                        <div style={s.field}>
                            <label style={s.label}>ID number <span style={s.req}>*</span></label>
                            <input style={s.input} placeholder="1xxxxxxxxx" value={visitForm.technician_id_number} onChange={e => setVisitForm({ ...visitForm, technician_id_number: e.target.value })} />
                        </div>
                    </div>
                    <div style={s.field}>
                        <label style={s.label}>Notes</label>
                        <textarea style={{ ...s.input, height: 70, padding: '8px 10px', resize: 'none' }} value={visitForm.notes} onChange={e => setVisitForm({ ...visitForm, notes: e.target.value })} />
                    </div>
                </Modal>
            </div>
        </div>
    )
}

const styles: Record<string, React.CSSProperties> = {
    page: { padding: '32px 24px', maxWidth: 1200, margin: '0 auto' },
    loading: { textAlign: 'center', padding: '60px', color: T.text3 },
    back: { background: 'none', border: 'none', color: T.accent, fontSize: 13, cursor: 'pointer', padding: 0, fontWeight: 500 },
    themeToggle: { width: 30, height: 30, borderRadius: 8, background: T.surface2, border: `0.5px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: T.text2 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },

    ticketNum: { fontSize: 13, color: T.text3, fontWeight: 600 },
    badge: { display: 'inline-block', padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600 },
    title: { fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 6 },
    meta: { fontSize: 13, color: T.text2 },
    grid: { display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, alignItems: 'flex-start' },
    main: { display: 'flex', flexDirection: 'column', gap: 16 },
    sidebar: { display: 'flex', flexDirection: 'column', gap: 16 },
    card: { background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 10, padding: 16 },
    cardLabel: { fontSize: 11, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 },
    btnSecondary: { height: 34, padding: '0 14px', background: 'transparent', color: T.text2, border: `0.5px solid ${T.borderStrong}`, borderRadius: 8, fontSize: 12, cursor: 'pointer' },
    field: { marginBottom: 12 },
    label: { display: 'block', fontSize: 12, fontWeight: 500, color: T.text2, marginBottom: 5 },
    input: { width: '100%', height: 36, border: `0.5px solid ${T.borderStrong}`, borderRadius: 8, padding: '0 10px', fontSize: 13, color: T.text, outline: 'none', background: T.surface },
    req: { color: T.danger, marginLeft: 2 },
    visitLabel: { fontSize: 10, color: T.text3, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 },
    visitVal: { fontSize: 13, color: T.text, fontWeight: 500 },
}
